import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { useNetworkStore } from '@shared/network';
import { stagePosKey, type FirstCardNote, type SessionEngine } from '../../model';
import type { SessionVariant } from '../variant';
import { AnswerDock } from './AnswerDock';
import { McqOptions } from './McqOptions';
import { StageHero } from './StageHero';
import { StageHint, StageReveal } from './StageReveal';
import { StageProgress } from './StageProgress';
import { StageAction, StageFinish, StageTop, StreakChip } from './StageTop';

interface SessionStageProps {
  e: SessionEngine;
  variant: SessionVariant;
  /** Подсказка, пока в тренировке не дано ни одного ответа (знакомство). */
  firstCardNote?: FirstCardNote;
}

/** Тон строки ошибки: «мягкие» (повтори/подожди) — жёлтый, отказы сети и
 *  сервера — красный, «связь вернулась» — зелёный. */
function bannerTone(kind: string, reconnected: boolean): 'ok' | 'warn' | 'danger' {
  if (reconnected) return 'ok';
  return kind === 'rate_limited' || kind === 'embed_unavailable' || kind === 'too_long'
    ? 'warn'
    : 'danger';
}

/**
 * Этап упражнений. Плашек-карточек нет: задание, слово и ответ живут на одной
 * сцене — сверху прогресс, по центру вопрос, снизу док ответа. Вся логика
 * (очередь, вердикты, комбо, подсказки, авто-переход) приходит из движка.
 */
export function SessionStage({ e, variant, firstCardNote }: SessionStageProps) {
  const { t } = useTranslation();
  const card = e.card;
  const exercise = e.exercise;
  const [typed, setTyped] = useState('');
  const prevPhase = useRef(e.phase);
  const netStatus = useNetworkStore((s) => s.status);
  const setBannerSuppressed = useNetworkStore((s) => s.setBannerSuppressed);
  // Ответ не ушёл из-за сети — об этом говорит строка на месте, а общая плашка сети в это время не
  // показывается ни в каком статусе: два сообщения об одном, и на телефоне плашка закрывала шапку (B13).
  const networkRow = e.submitError?.kind === 'network' && e.phase !== 'feedback';
  // Сеть вернулась, а ответ не отправлен — та же строка зовёт отправить его ещё раз. Отправляет сам
  // ученик: автоповтор POST мог бы засчитать ответ дважды. Сетевой сбой интерцептор отмечает в сторе
  // до того, как ошибка дойдёт сюда, поэтому online здесь — уже вернувшаяся связь.
  const reconnected = networkRow && (netStatus === 'reconnected' || netStatus === 'online');
  // Повторная отправка после сетевой ошибки: строка на время запроса уходит, но плашка не должна
  // мелькнуть — держим её скрытой, пока не придёт вердикт или другая ошибка.
  const [networkRetry, setNetworkRetry] = useState(false);
  useEffect(() => {
    if (networkRow) setNetworkRetry(true);
    else if (e.phase !== 'submitting') setNetworkRetry(false);
  }, [networkRow, e.phase]);
  const suppressBanner = networkRow || (networkRetry && e.phase === 'submitting');

  useLayoutEffect(() => {
    if (!suppressBanner) return;
    setBannerSuppressed(true);
    return () => {
      setBannerSuppressed(false);
      // «Связь вернулась» строка уже сказала — общая плашка «Связь восстановлена» после неё не нужна.
      const network = useNetworkStore.getState();
      if (network.status === 'reconnected') network.setStatus('online');
    };
  }, [suppressBanner, setBannerSuppressed]);

  // Сброс ввода на смене карточки и при возврате feedback→answering (повтор той
  // же карточки: itemId не меняется). На пути submitting→answering (сетевая
  // ошибка, rate-limit) текст СОХРАНЯЕМ — иначе юзер набирает его заново.
  useEffect(() => setTyped(''), [card?.itemId]);
  useEffect(() => {
    if (e.phase === 'answering' && prevPhase.current === 'feedback') setTyped('');
    prevPhase.current = e.phase;
  }, [e.phase]);

  if (!card || !exercise) {
    return (
      <div className="lx-stage">
        <div className="st-body st-body-plain">…</div>
      </div>
    );
  }

  const answered = e.phase === 'feedback';
  const good = e.outcome === 'good';
  const mood = answered ? (good ? 'good' : 'bad') : 'idle';
  const busy = e.finishing || e.phase === 'submitting';
  const hasHint = exercise === 'cloze' || exercise === 'reverse' || !!card.hint?.trim();
  const total = Math.max(e.dotStates.length, e.totalCards);
  // Только до первого ответа: после него подсказка уже не нужна, а при возобновлении
  // с середины тренировки её не будет.
  const firstNote =
    !answered && e.dotStates.every((s) => s === 'pending') ? firstCardNote?.[exercise] : undefined;
  const posKey = stagePosKey(card.pos);
  // Прямой перевод по предложению: переводят только выделенное слово (владелец 2026-10-29 — ученица
  // перевела фразу целиком, получила «неверно» и бросила первую тренировку).
  const wordInSentence = exercise === 'input' && !!card.simpleExample;

  const submit = () => {
    if (e.phase !== 'answering') return;
    const text = typed.trim();
    if (!text) return;
    e.onAnswer(text);
  };

  return (
    <div
      className={cn('lx-stage', e.shake && 'is-shake')}
      data-ex={exercise}
      data-mood={mood}
      data-hint={e.hintOpen && !answered ? 'open' : undefined}
    >
      <span className="st-wash" aria-hidden />
      <StageTop
        left={<StreakChip streak={e.streak} combo={e.combo} />}
        center={
          <StageProgress
            states={e.dotStates}
            current={e.currentIdx >= 0 ? e.currentIdx : 0}
            total={total}
            answered={answered}
          />
        }
        right={
          <>
            <StageAction onClick={e.onExit} disabled={busy}>
              {t('session.exitShort', 'выйти')}
            </StageAction>
            <StageFinish onFinish={e.onFinish} disabled={busy} finishing={e.finishing} />
          </>
        }
      />

      {e.submitError && e.phase !== 'feedback' && (
        <div
          className={cn('st-banner', bannerTone(e.submitError.kind, reconnected))}
          role={reconnected ? 'status' : 'alert'}
        >
          {reconnected ? t('session.answerError.reconnected') : t(e.submitError.key, e.submitError.params)}
        </div>
      )}

      <main className="st-body" key={card.itemId}>
        <div className="st-task">
          {wordInSentence ? t('session.task.inputWord') : t(`session.task.${exercise}`)}
          {/* Пробел снаружи: при переносе «· сущ.» уходит на новую строку целиком (nowrap у .st-pos). */}
          {posKey && (
            <>
              {' '}
              <span className="st-pos">· {t(posKey)}</span>
            </>
          )}
        </div>

        {/* Ручная карточка (без AI): объясняем, почему нет подсказки, предложения
            и упражнений в контексте — иначе это выглядит как поломка. */}
        {!card.isLlmGenerated && (
          <div className="st-manual-note" role="note">
            <span className="st-manual-ic" aria-hidden>
              ✎
            </span>
            <span>
              <b>{t('session.manualNote.title')}</b> {t('session.manualNote.body')}
            </span>
          </div>
        )}

        {firstNote && (
          <div className="st-manual-note" role="note">
            <span className="st-manual-ic" aria-hidden>
              ?
            </span>
            <span>{firstNote}</span>
          </div>
        )}

        <StageHero
          card={card}
          exercise={exercise}
          answered={answered}
          gaveUp={e.gaveUp}
          good={good}
          hintOpen={e.hintOpen}
          typed={typed}
        />

        {exercise === 'mcq' && (
          <McqOptions
            card={card}
            phase={e.phase}
            gaveUp={e.gaveUp}
            onAnswer={e.onAnswer}
            withDigits={variant === 'desktop'}
          />
        )}

        <div className="st-after">
          {/* reverse не показывает строку-объяснение: там подсказка — ровно первая
              буква, и она уже открыта в замаскированном слове выше. */}
          {e.hintOpen && !answered && hasHint && exercise !== 'reverse' && (
            <StageHint card={card} exercise={exercise} />
          )}
          {answered && (
            <StageReveal
              card={card}
              exercise={exercise}
              good={good}
              offSenseHint={e.gaveUp ? null : e.offSenseHint}
              contextNote={e.gaveUp ? null : e.contextNote}
              wholeSentence={!e.gaveUp && e.wholeSentence}
            />
          )}
        </div>
      </main>

      <AnswerDock
        exercise={exercise}
        phase={e.phase}
        checking={e.phase === 'submitting' && e.outcome === null && !e.finishing}
        outcome={e.outcome}
        matchedVia={e.matchedVia}
        gaveUp={e.gaveUp}
        hintOpen={e.hintOpen}
        hasHint={hasHint}
        awaitingContinue={e.awaitingContinue}
        autoAdvanceMs={e.autoAdvanceMs}
        typed={typed}
        onTyped={setTyped}
        onSubmit={submit}
        onGiveUp={e.onGiveUp}
        onHint={e.toggleHint}
        onContinue={e.onContinue}
        inputRef={e.inputRef}
        variant={variant}
        wordInSentence={wordInSentence}
      />
    </div>
  );
}
